import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadPageContent, handlePageLinkClick } from '../utils/pageHelpers';
import { PageSkeleton } from '../components/Loading';

interface AboutPageProps {
  subpage?: string;
}

const AboutPage: React.FC<AboutPageProps> = ({ subpage = 'about' }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const fullContent = await loadPageContent(`/cloneTarget/www.keso.kr/company/${subpage}.html`);
        setContent(fullContent);
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [subpage]);

  useEffect(() => {
    if (!contentRef.current) return;

    const contentElement = contentRef.current;
    const linkClickHandler = (e: Event) => handlePageLinkClick(e, navigate);

    contentElement.addEventListener('click', linkClickHandler, true);
    window.scrollTo(0, 0);

    // Fix Cloudflare email protection
    const fixCloudflareEmails = () => {
      // Find all __cf_email__ elements
      const cfEmails = contentElement.querySelectorAll('.__cf_email__');
      cfEmails.forEach((el) => {
        const span = document.createElement('span');
        span.setAttribute('data-email', 'kesti2026');
        span.setAttribute('data-domain', 'daum.net');
        el.parentNode?.replaceChild(span, el);
      });

      // Find all email protection links
      const cfLinks = contentElement.querySelectorAll('a[href*="/cdn-cgi/l/email-protection"]');
      cfLinks.forEach((el) => {
        const span = document.createElement('span');
        span.setAttribute('data-email', 'kesti2026');
        span.setAttribute('data-domain', 'daum.net');
        el.parentNode?.replaceChild(span, el);
      });

      // Replace [email protected] text nodes
      const walker = document.createTreeWalker(
        contentElement,
        NodeFilter.SHOW_TEXT,
        null
      );

      const textNodesToReplace: { node: Node; parent: Node }[] = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent?.includes('[email') && node.textContent?.includes('protected]')) {
          textNodesToReplace.push({ node, parent: node.parentNode! });
        }
      }

      textNodesToReplace.forEach(({ node, parent }) => {
        const span = document.createElement('span');
        span.setAttribute('data-email', 'kesti2026');
        span.setAttribute('data-domain', 'daum.net');
        parent.replaceChild(span, node);
      });
    };

    // Run email fix after a short delay to ensure DOM is ready
    setTimeout(fixCloudflareEmails, 100);

    return () => {
      contentElement.removeEventListener('click', linkClickHandler, true);
    };
  }, [content, navigate]);

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <>
      <style>{`
        .about_right .permit-images {
          display: flex;
          gap: 15px;
          margin-bottom: 15px;
        }

        .about_right .permit-images img {
          width: 240px;
          height: auto;
          border: 1px solid #ddd;
        }

        @media (max-width: 768px) {
          .about_right .permit-images {
            gap: 10px;
          }

          .about_right .permit-images img {
            width: 150px;
          }
        }

        @media (max-width: 480px) {
          .about_right .permit-images {
            flex-direction: column;
            align-items: center;
            gap: 10px;
          }

          .about_right .permit-images img {
            width: 200px;
            max-width: 90%;
          }
        }

        /* CI Page Styles */
        .ci_list {
          text-align: center !important;
          list-style: none !important;
          padding: 0 !important;
          margin: 40px auto !important;
          display: flex !important;
          justify-content: center !important;
          align-items: flex-end !important;
          gap: 40px !important;
          flex-wrap: wrap !important;
        }

        .ci_list li {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          margin: 0 !important;
        }

        .ci_list li img {
          display: block !important;
          height: 150px !important;
          width: auto !important;
          object-fit: contain !important;
          margin-bottom: 10px !important;
        }

        .ci_list li p {
          text-align: center !important;
          margin: 0 !important;
          font-weight: bold !important;
          font-size: 16px !important;
        }

        @media (max-width: 768px) {
          .ci_list {
            flex-direction: column !important;
            align-items: center !important;
            gap: 30px !important;
          }

          .ci_list li img {
            height: 120px !important;
          }
        }

        /* Chairman Message Page Styles */
        .chairman_wrap .chair_p03,
        .chairman_wrap .chair_p04 {
          text-align: left !important;
        }

        .chairman_wrap .chair_p01,
        .chairman_wrap .chair_p02 {
          text-align: center !important;
        }
      `}</style>
      <div ref={contentRef} dangerouslySetInnerHTML={{ __html: content }} />
    </>
  );
};

export default AboutPage;
